import { GalleryArtEligibilityNotice } from './GalleryArtEligibilityNotice';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone } from 'lucide-react';
import { GalleryFutureEntryGuide } from './GalleryFutureEntryGuide';
import { GalleryFeeSchedule } from './GalleryFeeSchedule';
import {galleryOpeningText} from './galleryParticipationCopy';


const faqs = (applicationsOpen, availabilityKnown) => [
  ["Can I apply right now?", !availabilityKnown ? "Check the application availability message at the top of this page. If it could not load, use Try again." : applicationsOpen ? "Yes. Use Apply for a Gallery Rotation to sign in, choose your preferred rotation, and start or continue your application." : "Applications are not currently available. You can sign in to view existing applications."],
  ["Does being selected guarantee my work will be displayed?", "No. Space is assigned separately once a rotation is confirmed, so being selected does not guarantee an immediate space or that every submitted piece will be displayed."],
  ["What does it cost to apply and participate?", "New ongoing gallery applications are free. Participation requires six service hours per month once accepted — or three $50 monthly payments ($150 total), or one $150 payment for the three-month rotation, for members who cannot commit to the monthly shift. Coppini keeps a 25% commission on sales of original art; artists keep 75%. There is no monthly wall rent."],
  ["When is the gallery open?", galleryOpeningText],
  ["Where can I see past exhibitions?", "Past exhibitions are kept in a separate archive, linked below."],
];


export const GalleryInformation = ({ address, policies, applicationsOpen = false, availabilityKnown = true }) => (
  <>
    <section className="border-y border-[#c8cbd1] bg-white" data-testid="gallery-service-section">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-[0.8fr_1.2fr] md:px-8 md:py-24">
        <div>
          <p className="text-xs font-semibold uppercase text-[#7c2434]">Ongoing Gallery Rotations</p>
          <h2 className="mt-3 font-gallery-heading text-3xl text-[#17243d]">A member-run cooperative</h2>
        </div>
        <div className="text-sm leading-7 text-[#5d6470]">
          <p>
            Participating artists keep the Di Barbieri Gallery running together. Each artist commits to one
            six-hour shift per month. Members who cannot commit to that shift may pay three $50 monthly
            payments ($150 total), or one $150 payment for the three-month rotation, for staffing coverage.
          </p>
          <p className="mt-5">
            Rotations run three months. Coppini retains a 25% commission on sales of original art &mdash; artists
            keep 75% &mdash; and there is no monthly wall rent.
          </p>
        </div>
      </div>
    </section>

    <section className="bg-[#f4f5f7]" data-testid="gallery-artwork-requirements">
      <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
        <h2 className="font-gallery-heading text-3xl text-[#17243d]">Ongoing rotation artwork and wall space</h2><GalleryArtEligibilityNotice compact />
        <ul className="mt-6 list-disc space-y-3 pl-5 text-sm leading-7 text-[#313a4a]">
          <li>Submit up to six wall works under 9 × 12 inches, or up to four no larger than 20 × 16 inches.</li>
          <li>Use the outside frame measurements for framed work and the artwork measurements for unframed work. Portrait and landscape orientations are allowed. Staff check hanging readiness separately.</li>
          <li>Plan for a 4 ft × 4 ft wall section per artist (16 square feet), including spacing. Gallery staff confirm the hanging layout.</li>
          <li>The board and board consultant review portfolios and individual artworks.</li>
        </ul>
        <p className="mt-6 text-sm leading-7 text-[#313a4a]" data-testid="gallery-first-rotation-month">These requirements apply to Ongoing Gallery Rotations. Staff provide rotation display, delivery and pickup dates with confirmed space assignments. The separate Member Holiday Show opens December 5, 2026, and remains on display through December; its separate entry deadline is November 21, 2026. Up to three paintings at $5 each; delivery is the morning of December 4 and the reception is December 5, 5–8 p.m. Pickup instructions will be confirmed by staff.</p>
      </div>
    </section>

    <GalleryFutureEntryGuide applicationsOpen={applicationsOpen} availabilityKnown={availabilityKnown} />

    <GalleryFeeSchedule applicationsOpen={applicationsOpen} availabilityKnown={availabilityKnown} />

    <section className="bg-white" data-testid="gallery-selection-note-section">
      <div className="mx-auto max-w-6xl px-5 py-12 md:px-8">
        <div className="border-l-4 border-[#7c2434] bg-[#f4f5f7] px-5 py-5 md:px-8" data-testid="gallery-selection-note">
          <p className="text-sm leading-7 text-[#313a4a]">
            Jury selection does not guarantee an immediate space, or that every submitted piece will be displayed
            &mdash; space is assigned separately once a rotation is confirmed. Once a space is confirmed, gallery
            staff schedule delivery, record each piece's condition on arrival, install it, display it, and later
            record its removal and confirm pickup.
          </p>
        </div>
      </div>
    </section>

    <section className="bg-[#f4f5f7]" data-testid="gallery-faq-section">
      <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
        <h2 className="font-gallery-heading text-3xl text-[#17243d]">Frequently asked questions</h2>
        <div className="mt-10 grid gap-x-12 gap-y-9 md:grid-cols-2">
          {faqs(applicationsOpen, availabilityKnown).map(([question, answer]) => (
            <div key={question} className="border-t border-[#9da3ae] pt-5">
              <h3 className="font-gallery-display text-xl text-[#17243d]">{question}</h3>
              <p className="mt-3 text-sm leading-6 text-[#5d6470]">{answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white" data-testid="gallery-visit-section">
      <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
        <div className="flex flex-col justify-between gap-10 md:flex-row md:items-start">
          <div>
            <p className="text-xs font-semibold uppercase text-[#7c2434]">Visit at the Coppini</p>
            <p className="mt-3 flex items-start gap-3 font-gallery-display text-2xl text-[#17243d]" data-testid="gallery-visiting-address">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#7c2434]" /> {address}
            </p>
            <h2 className="mt-5 font-gallery-heading text-2xl text-[#17243d]">Gallery opening days</h2>
            <p className="mt-3 max-w-xl text-base leading-7 text-[#313a4a]" data-testid="gallery-visiting-schedule">{galleryOpeningText}</p>
            <div className="mt-5 flex flex-col gap-2 text-sm text-[#313a4a]" data-testid="gallery-academy-contact">
              <a href="tel:+12105308445" className="inline-flex items-center gap-2 hover:text-[#7c2434]" data-testid="gallery-contact-phone">
                <Phone className="h-4 w-4 text-[#7c2434]" /> (210) 530-8445
              </a>
              <a href="mailto:info@thecoppini.org" className="inline-flex items-center gap-2 hover:text-[#7c2434]" data-testid="gallery-contact-email">
                <Mail className="h-4 w-4 text-[#7c2434]" /> info@thecoppini.org
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 font-semibold text-[#7c2434] hover:text-[#17243d]" data-testid="gallery-contact-link">
                Academy Contact page <ArrowRight size={15} />
              </Link>
            </div>
          </div>
          <Link to="/exhibitions" className="inline-flex items-center gap-2 font-semibold text-[#7c2434] hover:text-[#17243d]" data-testid="gallery-past-exhibitions-link">
            Past Exhibitions <ArrowRight size={17} />
          </Link>
        </div>
      </div>
    </section>
  </>
);
